/** /tour — the product in screenshots, in the order a session actually runs. */
import { Header, Page } from "../components/Header";
import { Icon } from "../components/Icon";
import { useI18n } from "../i18n";
import { TOUR_FRAMES, type TourFrame } from "../lib/tour";
import { Link } from "../router";

/** A frame, its surface named, with a caption underneath. Sizes come from the manifest so nothing shifts on load. */
export function TourFigure({
  frame,
  eager,
  compact,
}: {
  frame: TourFrame;
  eager?: boolean;
  compact?: boolean;
}) {
  const { t } = useI18n();
  return (
    <figure class={`tour-fig is-${frame.surface}${compact ? " compact" : ""}`}>
      <div class="tour-shot">
        <img
          src={frame.src}
          width={frame.width}
          height={frame.height}
          alt={t(`tour.${frame.id}.alt`)}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
        />
      </div>
      <figcaption>
        <span class="eyebrow">{t(`tour.surface.${frame.surface}`)}</span>
        <strong>{t(`tour.${frame.id}.title`)}</strong>
        {compact ? null : <p class="fg-2">{t(`tour.${frame.id}.body`)}</p>}
      </figcaption>
    </figure>
  );
}

export function Tour() {
  const { t } = useI18n();
  return (
    <Page wide>
      <Header
        right={
          <Link href="/host" class="btn secondary sm">
            {t("nav.host")}
          </Link>
        }
      />
      <main id="main" class="tour">
        <header class="tour-head">
          <p class="eyebrow">{t("tour.eyebrow")}</p>
          <h1>{t("tour.title")}</h1>
          <p class="lead fg-2">{t("tour.intro")}</p>
          <div class="row">
            <a href="/r/example" class="btn" target="_blank" rel="noopener">
              <Icon name="file-text" /> {t("tour.example")}
            </a>
            <Link href="/host" class="btn secondary">
              {t("landing.cta.host")}
            </Link>
          </div>
        </header>
        <ol class="tour-list">
          {TOUR_FRAMES.map((frame, i) => (
            <li key={frame.id} class={`is-${frame.surface}`}>
              <span class="tour-n mono" aria-hidden="true">
                {String(i + 1).padStart(2, "0")}
              </span>
              <TourFigure frame={frame} eager={i < 2} />
            </li>
          ))}
        </ol>
        <footer class="tour-foot">
          <hr class="rule" />
          <p class="muted small">{t("tour.note")}</p>
        </footer>
      </main>
    </Page>
  );
}
